import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";

import styles from "../../assets/styles/componentStyles/billingStyles/changePlan.module.scss";

import agent from "../../services/api";
import Plans from "./billingPlansComponents/plans";
import AddOns from "./billingPlansComponents/addOns";
import BillingFilterTabs from "./billingPlansComponents/billingFilterTabs";
import PlanSummaryCard from "./billingPlansComponents/planSummaryCard";
import NextBillingDateCard from "./billingPlansComponents/nextBillingDateCard";
import PlanSubscribedPopup from "./popup/planSubscribedPopup";
import BillingInfoPopup from "./popup/billingInfoPopup";
import GlobalButton from "../utility/globalButton/button";
import { PricingPlanFeatureLimit, subscribePlanAddOn, subscribePlanPayload } from "../../models/axiosModel";
import { UserContext, UserContextPayload } from "../../context/userContext";
import { useGenericStore } from "../../store/genericStore";
import { useBillingStore } from "../../store/billingStore";
import { CURRENCY_TYPES, CYCLE_TYPES, PLANS, POLLING_DURATION, POLLING_INTERVAL } from "./constant.billing";
import { getChangePlanAddOnTotalAmount, getBillingCycleName, getBillingPriceSuffix, isFreePlan, parseQueryParams, QUERY_PARAM_KEYS, BillingId, channelDeploymentLimits, isCurrentPlanPublic } from "./utils.billing";
import { thousandSeparator } from "../../utils/constants";
import useGenericHook from "../../hooks/useGenericHook";
import ChannelDeploymentLimitPopup from "./popup/channelDeploymentLimitPopup";

const ChangePlan = () => {

    const [searchParams, setSearchParams] = useSearchParams();
    const { userDetail, setFlagLoading } = useContext(UserContext) as UserContextPayload;

    const [showChannelDeploymentLimitPopup, setShowChannelDeploymentLimitPopup] = useState(false);

    const { showError } = useGenericStore(useShallow((state) => ({
        showError: state.showError,
    })));

    const { pricingPlans, billingAddOns, billingCycle, billingCurrencySymbol, billingSubscriptionDetails, setBillingTabs, getBillingSubscriptionDetailsApi, isPricingPlansLoading } = useBillingStore(useShallow((state) => ({
        pricingPlans: state.pricingPlans,
        billingAddOns: state.billingAddOns,
        billingCycle: state.billingCycle,
        billingCurrencySymbol: state.billingCurrencySymbol,
        billingSubscriptionDetails: state.billingSubscriptionDetails,
        setBillingTabs: state.setBillingTabs,
        getBillingSubscriptionDetailsApi: state.getBillingSubscriptionDetailsApi,
        isPricingPlansLoading: state.isPricingPlansLoading,
    })));

    const { isScreenBelow600 } = useGenericHook();

    const query = useMemo(() => parseQueryParams(searchParams), [searchParams]);
    const queryAppliedRef = useRef(false);

    const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isPollingCancelledRef = useRef(false);

    const organizationId = useMemo(
        () => userDetail?.user_organization_data?.organization_id,
        [userDetail?.user_organization_data?.organization_id]
    );

    const currentPlanId = useMemo(
        () => billingSubscriptionDetails?.current_plan?.billing_plan_id,
        [billingSubscriptionDetails?.current_plan?.billing_plan_id]
    );

    const [subscribePayload, setSubscribePayload] = useState<subscribePlanPayload>({
        organization_id: organizationId ?? 0,
        billing_plan_id: "",
        add_ons: [],
    });

    const [initialAddOnsFromQuery, setInitialAddOnsFromQuery] = useState<subscribePlanAddOn[] | null>(null);
    const [preferredPlanId, setPreferredPlanId] = useState<BillingId | null>(query.plan);

    const [showPlanSubscribedSuccessPopup, setShowPlanSubscribedSuccessPopup] = useState(false);
    const [showRefreshStatusPopup, setShowRefreshStatusPopup] = useState(false);
    const [showPaymentProcessingPopup, setShowPaymentProcessingPopup] = useState(false);

    // Channel Deployment Limits
    const channelDeploymentLimitsData = useMemo(() => channelDeploymentLimits(billingSubscriptionDetails), [billingSubscriptionDetails]);

    const isConnectedChannelsWithinLimit = useMemo(() => {
        
        const currentFeatures = channelDeploymentLimitsData?.features || [];
        const selectedPlanLimits = pricingPlans?.find((plan) => String(plan.billing_plan_id) === String(subscribePayload.billing_plan_id))
            ?.features?.["Channel Deployment Limits"] || [];
    
        return currentFeatures.every((currentFeature) => {
            const selectedPlanLimit = selectedPlanLimits.find(
                (limit: PricingPlanFeatureLimit) => String(limit.billing_feature_id) === String(currentFeature.billing_feature_id)
            );
    
            if (!selectedPlanLimit) {
                return false;
            }
    
            return (Number(currentFeature.feature_used_amount) <= Number(selectedPlanLimit.billing_plan_feature_limit));
        });
    }, [channelDeploymentLimitsData, pricingPlans, subscribePayload.billing_plan_id]);

    // Selected Plan
    const selectedPlan = useMemo(
        () => pricingPlans?.find((plan) => String(plan.billing_plan_id) === String(subscribePayload.billing_plan_id)),
        [pricingPlans, subscribePayload.billing_plan_id]
    );

    const isFreePlanSelected = !selectedPlan || selectedPlan.billing_plan_name?.toLowerCase() === PLANS.FREE;

    // Billing Amounts
    const billingAmounts = useMemo(() => {
        const planAmount = selectedPlan?.billing_plan_amount || 0;
    
        let recurringTotal = planAmount;
        let oneTimeTotal = 0;
    
        (subscribePayload.add_ons || []).forEach((selectedAddOn) => {
            const addOn = billingAddOns?.find(
                (item) =>
                    String(item.billing_add_on_id) ===
                    String(selectedAddOn.billing_add_on_id)
            );
    
            if (!addOn) return;

            const amount = getChangePlanAddOnTotalAmount(addOn, selectedAddOn.quantity, billingCycle);
    
            if (selectedAddOn?.flag_recurring) {
                recurringTotal += amount;
            } else {
                oneTimeTotal += amount;
            }
        });
    
        return {
            recurringTotal,
            oneTimeTotal,
        };
    }, [selectedPlan, subscribePayload.add_ons, billingAddOns, billingCycle]);

    // Fetch Billing Subscription Details
    const fetchBillingSubscriptionDetails = async () => {
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));

        const res = await getBillingSubscriptionDetailsApi();

        if (!res?.status) {
            showError(res?.error?.message || "Something went wrong");
        }

        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
    };

    // Handle Select Plan
    const handleSelectPlan = useCallback((planId: BillingId) => {
        const selectingFree = isFreePlan(pricingPlans ?? [], planId);

        setSubscribePayload((prev) => ({
            ...prev,
            organization_id: organizationId ?? prev.organization_id,
            billing_plan_id: planId,
            add_ons: selectingFree ? [] : prev.add_ons,
        }));
    }, [pricingPlans, organizationId]);

    // Handle Add-ons Change
    const handleAddOnsChange = useCallback((addOns: subscribePlanAddOn[]) => {
        setSubscribePayload((prev) => {
            if (isFreePlan(pricingPlans ?? [], prev.billing_plan_id)) {
                return {
                    ...prev,
                    organization_id: organizationId ?? prev.organization_id,
                    add_ons: [],
                };
            }

            return {
                ...prev,
                organization_id: organizationId ?? prev.organization_id,
                add_ons: addOns,
            };
        });
    }, [pricingPlans, organizationId]);

    // Check Disable Checkout Button
    const isCheckoutDisabled = useMemo(() => {
        const planId = subscribePayload.billing_plan_id;
        return !planId || (planId === currentPlanId);
    }, [subscribePayload.billing_plan_id, currentPlanId]);

    // Handle Checkout
    const handleCheckout = async () => {

        if(!isConnectedChannelsWithinLimit) {
            setShowChannelDeploymentLimitPopup(true);
            return;
        }

        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));

        try {
            const res = await agent.subscribePlan.post(subscribePayload);

            if (!res?.status && !res?.data) {
                showError(res?.error?.message || "Something went wrong");
                return;
            }

            const responseData = Array.isArray(res.data) ? res.data[0] : res.data;
            const subscriptionUrl = responseData?.subscription_url;
            const billingSubscriptionId = responseData?.billing_subscription_id;

            if (subscriptionUrl) {
                setShowPaymentProcessingPopup(true);
                window.open(subscriptionUrl, "_blank", "noopener,noreferrer");

                // Reset cancellation flag before starting polling
                isPollingCancelledRef.current = false;

                // Start polling
                pollSubscriptionStatus(billingSubscriptionId);
            } else {
                await fetchBillingSubscriptionDetails();
                setShowPlanSubscribedSuccessPopup(true);
            }
        } catch (error) {
            showError((error as Error)?.message || "Something went wrong");
        } finally {
            setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
        }
    };

    // Polling
    const pollSubscriptionStatus = async (billingSubscriptionId: string) => {
        const startTime = Date.now();

        const poll = async () => {
            // Stop if component is unmounted
            if (isPollingCancelledRef.current) return;

            try {
                const res = await agent.getSubscriptionStatus.get(billingSubscriptionId);

                if (isPollingCancelledRef.current) return;

                if (res?.status && res?.data) {
                    const responseData = Array.isArray(res.data) ? res.data[0] : res.data;

                    const status = responseData?.subscription_status?.toLowerCase();

                    if (status === "authenticated" || status === "active") {
                        setShowPaymentProcessingPopup(false);
                        setShowPlanSubscribedSuccessPopup(true);
                        return;
                    }
                }
            } catch (error) {
                setShowPaymentProcessingPopup(false);
                console.error("Polling failed:", error);
            }

            // Stop after 2 minutes
            if (Date.now() - startTime >= POLLING_DURATION) {
                if (!isPollingCancelledRef.current) {
                    setShowPaymentProcessingPopup(false);
                    setShowRefreshStatusPopup(true);
                }
                return;
            }

            // Schedule next poll
            pollingTimeoutRef.current = setTimeout(poll, POLLING_INTERVAL);
        };

        poll();
    };


    // EFFECTS

    // Apply query params once when pricing plans are ready
    useEffect(() => {
        if (queryAppliedRef.current || !query.plan || !pricingPlans?.length) return;

        const plan = pricingPlans.find((item) => String(item.billing_plan_id) === String(query.plan));
        if (!plan) return;

        queryAppliedRef.current = true;

        const isFree = plan.billing_plan_name?.toLowerCase() === PLANS.FREE;
        const addOns = isFree ? [] : query.addOns;

        setBillingTabs(plan.billing_plan_cycle_period_name || CYCLE_TYPES.MONTHLY, plan.billing_plan_currency);
        setPreferredPlanId(plan.billing_plan_id);
        setSubscribePayload((prev) => ({
            ...prev,
            organization_id: organizationId ?? prev.organization_id,
            billing_plan_id: plan.billing_plan_id,
            add_ons: addOns,
        }));

        if (addOns.length) setInitialAddOnsFromQuery(addOns);

        const nextParams = new URLSearchParams(searchParams);
        Object.values(QUERY_PARAM_KEYS).forEach((key) => nextParams.delete(key));

        setSearchParams(nextParams, { replace: true });

    }, [pricingPlans, query, organizationId, setBillingTabs, searchParams, setSearchParams]);

    useEffect(() => {
        if (billingSubscriptionDetails) return;
        fetchBillingSubscriptionDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Align filter tabs to current plan so it exists in filteredPlans when auto-select runs
    useEffect(() => {
        if (queryAppliedRef.current || query.plan) return;

        const plan = billingSubscriptionDetails?.current_plan;
        if (!plan?.billing_plan_id) return;

        setBillingTabs(
            plan.billing_plan_cycle_period_name || CYCLE_TYPES.MONTHLY,
            plan.billing_plan_currency || CURRENCY_TYPES.INR
        );
    }, [billingSubscriptionDetails?.current_plan?.billing_plan_id, query.plan, setBillingTabs]);

    // Cancel polling on unmount
    useEffect(() => {
        return () => {
            isPollingCancelledRef.current = true;

            if (pollingTimeoutRef.current) {
                clearTimeout(pollingTimeoutRef.current);
            }
        };
    }, []);

    const upcomingPlan = billingSubscriptionDetails?.upcoming_plan;
    const hasNextBillingDate = !!billingSubscriptionDetails?.current_plan?.next_charge_at;
    const showSideCard = !!upcomingPlan || hasNextBillingDate;
    const showBillingFilterTabs = billingSubscriptionDetails?.current_plan && isCurrentPlanPublic(billingSubscriptionDetails?.current_plan);

    if (isPricingPlansLoading) {
        return <></>;
    }

    return (
        <Box className={styles.root}>
            <Grid container spacing={3}>
                
                {/* Billing Filter Tabs */}
                {showBillingFilterTabs && (
                    <Grid item xs={12}>
                        <BillingFilterTabs />
                    </Grid>
                )}

                {/* Current / Upcoming / Next Billing Summary */}
                <Grid item xs={12}>
                    <Grid container spacing={isScreenBelow600 ? 3 : 5}>
                        <Grid item xs={12} lg={6}>
                            <PlanSummaryCard
                                plan={billingSubscriptionDetails?.current_plan}
                                title="Current Active Plan"
                                addOnsLabel="Active Add-ons"
                                showPlanStartDate
                            />
                        </Grid>

                        {showSideCard && (
                            <Grid item xs={12} lg={6}>
                                {upcomingPlan ? (
                                    <PlanSummaryCard
                                        plan={upcomingPlan}
                                        title="Upcoming Plan"
                                        addOnsLabel="Upcoming Add-ons"
                                        showEffectiveFromDate
                                    />
                                ) : (
                                    <NextBillingDateCard
                                        plan={billingSubscriptionDetails?.current_plan}
                                        title="Next Billing Date"
                                    />
                                )}
                            </Grid>
                        )}
                    </Grid>
                </Grid>

                {/* Plans */}
                <Grid item xs={12}>
                    <Plans
                        selectedPlanId={subscribePayload.billing_plan_id || null}
                        preferredPlanId={preferredPlanId}
                        onSelectPlan={handleSelectPlan}
                    />
                </Grid>

                {/* Add-ons */}
                {!isFreePlanSelected && (
                    <Grid item xs={12}>
                        <AddOns
                            onAddOnsChange={handleAddOnsChange}
                            initialAddOns={initialAddOnsFromQuery}
                        />
                    </Grid>
                )}

                {/* Total Amount */}
                <Grid item xs={12}>
                    <Box className={styles.totalCard}>
                        <Box className={styles.totalLeft}>
                            <Typography className={styles.totalLabel}>
                                Total
                            </Typography>

                            <Typography className={styles.totalBillingNote}>
                                Billed {getBillingCycleName(billingCycle)}
                            </Typography>
                        </Box>

                        <Box className={styles.totalRight}>

                            {/* One-time Total */}
                            {billingAmounts.oneTimeTotal > 0 && (
                                <>
                                    <div className={styles.totalGroup}>
                                        <Typography className={styles.totalAmount}>
                                            {billingCurrencySymbol}
                                            {thousandSeparator(billingAmounts.oneTimeTotal)}
                                        </Typography>

                                        <Typography className={styles.totalSuffix}>
                                            one-time
                                        </Typography>
                                    </div>

                                    <Typography className={styles.totalSeparator}>
                                        +
                                    </Typography>
                                </>
                            )}

                            {/* Recurring Total */}
                            <div className={styles.totalGroup}>
                                <Typography className={styles.totalAmount}>
                                    {billingCurrencySymbol}
                                    {thousandSeparator(billingAmounts.recurringTotal)}
                                </Typography>

                                <Typography className={styles.totalSuffix}>
                                    {getBillingPriceSuffix(billingCycle)}
                                </Typography>
                            </div>
                        </Box>

                    </Box>
                </Grid>

                {/* Checkout Button */}
                <Grid item xs={12}>
                    <GlobalButton
                        buttonText={isFreePlanSelected ? "Change Plan" : "Proceed to Checkout"}
                        className="primaryButtonStyle"
                        onClick={() => handleCheckout()}
                        disabled={isCheckoutDisabled}
                        variant="contained"
                    />
                </Grid>
            </Grid>

            {/* Plan Subscribed Success Popup */}
            <PlanSubscribedPopup
                open={showPlanSubscribedSuccessPopup}
                planName={selectedPlan?.billing_plan_name}
                onClose={() => setShowPlanSubscribedSuccessPopup(false)}
                onContinue={() => { window.location.href = "/billing-usage"; }}
            />

            {/* Refresh Status Popup */}
            <BillingInfoPopup
                open={showRefreshStatusPopup}
                onClose={() => { setShowRefreshStatusPopup(false); }}
                onClickOfBtn={() => { window.location.reload(); setShowRefreshStatusPopup(false); }}
                title="Subscription Status"
                description="We couldn't confirm your payment yet. Click OK to refresh and check the latest status of your plan."
                buttonText="OK"
                showCloseIcon={true}
            />

            {/* Payment Processing Popup */}
            <BillingInfoPopup
                open={showPaymentProcessingPopup}
                onClose={() => setShowPaymentProcessingPopup(false)}
                title="Payment Processing"
                description="Complete your payment to continue. This page will update automatically once your payment is confirmed."
                showCloseIcon={true}
            />

            {/* Channel Deployment Limit Popup */}
            <ChannelDeploymentLimitPopup
                open={showChannelDeploymentLimitPopup}
                onClose={() => setShowChannelDeploymentLimitPopup(false)}
            />
        </Box>
    );
};

export default ChangePlan;